/**
 * Browser-side RFC 9421 HTTP Message Signature signing for direct agent auth.
 *
 * This is the in-browser counterpart of the httpsig-gateway signer
 * (src/web/httpsig-gateway/src/signer.ts). It deliberately mirrors that
 * signer's covered fields and signify-ts signing path so the agent's
 * SignatureValidationComponent verifies requests identically regardless of
 * whether the gateway or the browser produced the signature. See the
 * `Browser Side Direct Signing` decision (id wqz7k3mnbp) in this.i.
 *
 * Key difference from the gateway: the gateway loads a raw Ed25519 seed from a
 * mounted secret, whereas here the seed is derived in-page from a user-entered
 * PASSCODE via Salter (Argon2id, libsodium). Content-Digest is computed with
 * Web Crypto (crypto.subtle) instead of node:crypto.
 */

import {
  ready,
  MtrDex,
  Salter,
  Signer,
  Tier,
  Signage,
  siginput,
  signature as keriSignature,
  randomPasscode,
} from 'signify-ts';

export type HttpSigHeaders = Record<string, string>;

export interface DerivedIdentity {
  /** signify-ts Signer derived from the passcode; signs the request. */
  signer: Signer;
  /** Non-transferable AID (qb64, "B…") used as Signify-Resource and keyid. */
  aid: string;
}

export interface SignRequestInput {
  identity: DerivedIdentity;
  method: string;
  /** Absolute URL or origin-form path ("/v1/shim/…"); only the path is signed. */
  url: string;
  body?: string | Uint8Array;
  /** Extra request headers to carry through (lower-cased on output). */
  headers?: HttpSigHeaders;
  /** Override the covered components; defaults to DEFAULT_FIELDS. */
  fields?: string[];
}

const DEFAULT_LABEL = 'signify';

/**
 * Covered components — identical to the gateway's RequestSigner.DefaultFields,
 * which in turn match the agent's OriginAuthenticater.DefaultFields.
 */
export const DEFAULT_FIELDS = [
  '@method',
  '@path',
  'origin-date',
  'signify-resource',
  'content-digest',
];

/**
 * Fixed key-derivation parameters. These define which AID a given passcode maps
 * to; they are deterministic and MUST NOT change silently — a different value
 * here would derive a different AID for the same passcode, breaking every
 * already-authorized controller. (See this.i wqz7k3mnbp.)
 */
const DERIVATION = {
  /** Pad char between the Salt_128 code and the 21-char bran (signify convention). */
  saltPad: 'A',
  seedCode: MtrDex.Ed25519_Seed,
  transferable: false, // → Ed25519N verfer ("B…"), a non-transferable AID
  path: '',
  tier: Tier.low,
  temp: false,
};

/** Length of a keripy/signify-style passcode ("bran"), as randomPasscode() emits. */
export const PASSCODE_LENGTH = 21;

/** Generate a fresh keripy-style passcode (21-char bran). */
export async function generatePasscode(): Promise<string> {
  await ready();
  return randomPasscode();
}

/**
 * Compose the 24-char Salt_128 qb64 from a 21-char bran, mirroring signify-ts's
 * Controller: `Salt_128 + pad + bran`. Exported for unit testing.
 */
export function passcodeToSaltQb64(passcode: string): string {
  return MtrDex.Salt_128 + DERIVATION.saltPad + passcode;
}

function assertValidPasscode(passcode: string): void {
  if (typeof passcode !== 'string' || passcode.length !== PASSCODE_LENGTH) {
    throw new Error(
      `Invalid passcode: expected a ${PASSCODE_LENGTH}-character string`,
    );
  }
  // base64url alphabet — randomPasscode() emits only these characters.
  if (!/^[A-Za-z0-9_-]+$/.test(passcode)) {
    throw new Error('Invalid passcode: contains non-base64url characters');
  }
}

/**
 * Derive a non-transferable signing identity from a passcode. Deterministic:
 * the same passcode always yields the same AID.
 */
export async function deriveIdentity(passcode: string): Promise<DerivedIdentity> {
  assertValidPasscode(passcode);
  await ready();
  const salter = new Salter({ qb64: passcodeToSaltQb64(passcode) });
  const signer = salter.signer(
    DERIVATION.seedCode,
    DERIVATION.transferable,
    DERIVATION.path,
    DERIVATION.tier,
    DERIVATION.temp,
  );
  return { signer, aid: signer.verfer.qb64 };
}

function normalizeHeaders(headers: HttpSigHeaders = {}): HttpSigHeaders {
  const normalized: HttpSigHeaders = {};
  for (const [name, value] of Object.entries(headers)) {
    if (/[\r\n]/.test(value)) {
      throw new Error(`Invalid header value for ${name}: CR/LF is not allowed`);
    }
    normalized[name.toLowerCase()] = value;
  }
  return normalized;
}

function parsePath(inputUrl: string): string {
  if (/^https?:\/\//i.test(inputUrl)) {
    return new URL(inputUrl).pathname;
  }
  if (!inputUrl.startsWith('/')) {
    throw new Error("url must be an absolute URL or origin-form path starting with '/'");
  }
  if (inputUrl.includes('#')) {
    throw new Error('url must not contain a fragment component');
  }
  const qIndex = inputUrl.indexOf('?');
  return qIndex === -1 ? inputUrl : inputUrl.slice(0, qIndex);
}

/** Origin-compatible timestamp format expected by upstream integrations. */
function buildOriginDate(): string {
  return new Date().toISOString().replace('Z', '000+00:00');
}

function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function computeContentDigest(body: string | Uint8Array): Promise<string> {
  const bytes = typeof body === 'string' ? new TextEncoder().encode(body) : body;
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return `sha-256=:${toBase64Url(new Uint8Array(hash))}:`;
}

/**
 * Sign a request and return the full set of headers (carried-through headers
 * plus signify-resource, origin-date, content-digest, signature-input and
 * signature). Merge the result onto the outgoing request.
 */
export async function signRequest(input: SignRequestInput): Promise<HttpSigHeaders> {
  await ready();

  const method = input.method.toUpperCase();
  const path = parsePath(input.url);
  const fields = input.fields ?? DEFAULT_FIELDS;
  const body = input.body ?? '';
  const { signer, aid } = input.identity;

  const headers = normalizeHeaders(input.headers);

  if (
    headers['signify-resource'] &&
    headers['signify-resource'] !== aid &&
    fields.includes('signify-resource')
  ) {
    throw new Error('signify-resource header does not match signing identity');
  }
  headers['signify-resource'] = aid;

  if (fields.includes('origin-date')) {
    headers['origin-date'] = buildOriginDate();
  }
  if (fields.includes('content-digest') && !headers['content-digest']) {
    headers['content-digest'] = await computeContentDigest(body);
  }

  const webHeaders = new Headers();
  for (const [k, v] of Object.entries(headers)) {
    webHeaders.set(k, v);
  }

  const [sigInputMap, sig] = siginput(signer, {
    name: DEFAULT_LABEL,
    method,
    path,
    headers: webHeaders,
    fields,
    alg: 'ed25519',
    keyid: aid,
  });

  const signage = new Signage(new Map([[DEFAULT_LABEL, sig]]), false);
  const sigHeaders = keriSignature([signage]);

  const result: HttpSigHeaders = { ...headers };
  sigInputMap.forEach((v: string, k: string) => {
    result[k.toLowerCase()] = v;
  });
  sigHeaders.forEach((v: string, k: string) => {
    result[k.toLowerCase()] = v;
  });
  return result;
}
